import React, { useState, useRef, useEffect } from 'react';
import { ChatMessage, QUICK_ACTIONS, ViewMode } from '../types';
import { Send, Paperclip, Loader, MessageCircle, Bot, RefreshCw } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import CaseSelectionModal from './CaseSelectionModal';

interface ChatInterfaceProps {
  messages: ChatMessage[];
  isLoading: boolean;
  onSendMessage: (message: string) => void;
  onImageUpload: (files: File[]) => void;
  onViewModeChange?: (mode: ViewMode) => void;
  selectedTreatment?: { option: number; label: string } | null;
  showQuestionModal?: boolean;
  onCloseQuestionModal?: () => void;
  isAskQuestionsMode?: boolean;
  sessionId?: string | null;
  autoShowDraftPlan?: boolean;
  onResetChat?: () => void;
}

const ChatInterface: React.FC<ChatInterfaceProps> = ({
  messages,
  isLoading,
  onSendMessage,
  onImageUpload,
  onViewModeChange,
  selectedTreatment,
  showQuestionModal: propShowQuestionModal,
  onCloseQuestionModal,
  isAskQuestionsMode = false,
  autoShowDraftPlan = false,
  onResetChat
}) => {
  const [inputValue, setInputValue] = useState('');
  const [showCaseModal, setShowCaseModal] = useState(false);
  const [showQuestionModal, setShowQuestionModal] = useState(false);
  const [showGeneratePlanButton, setShowGeneratePlanButton] = useState(false);
  const [showDraftPlanModal, setShowDraftPlanModal] = useState(false);
  const [showCustomCaseModal, setShowCustomCaseModal] = useState(false);
  const [customCaseText, setCustomCaseText] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Handle question modal from parent
  useEffect(() => {
    if (propShowQuestionModal) {
      setShowQuestionModal(true);
    }
  }, [propShowQuestionModal]);

  // Auto-show Draft Plan modal when Treatment Plan is clicked from sidebar
  useEffect(() => {
    if (autoShowDraftPlan && messages.length === 0) {
      setShowDraftPlanModal(true);
    }
  }, [autoShowDraftPlan, messages.length]);

  // Auto-scroll to bottom when new messages arrive
  // Use a ref to track previous message count to avoid unnecessary scrolls
  const prevMessageCount = useRef(messages.length);
  
  useEffect(() => {
    // Only scroll if we have MORE messages than before (new message added)
    if (messages.length > prevMessageCount.current) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
    prevMessageCount.current = messages.length;
  }, [messages]);

  // Check for criteria completion
  useEffect(() => {
    // Check if criteria is complete from the latest assistant message
    if (messages.length > 0) {
      const lastMessage = messages[messages.length - 1];
      if (lastMessage.role === 'assistant') {
        // Check for criteria completion indicators
        const isCriteriaComplete = 
          (lastMessage.action === 'criteria_complete_with_options') ||
          (lastMessage.metadata?.case_resolved === true) ||
          (lastMessage.content.includes('Now that we have all this information')) ||
          (lastMessage.content.includes('What would you like to do next?') && 
           lastMessage.content.includes('Generate a detailed treatment plan'));
           
        setShowGeneratePlanButton(isCriteriaComplete && !lastMessage.content.includes('Treatment Decisions:'));
      }
    }
  }, [messages]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (inputValue.trim() && !isLoading) {
      onSendMessage(inputValue);
      setInputValue('');
    }
  };


  const handleCaseSelect = (caseDescription: string) => {
    onSendMessage(caseDescription);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const imageFiles = files.filter(file => file.type.startsWith('image/'));
    
    if (imageFiles.length > 0) {
      onImageUpload(imageFiles);
    }
    
    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const formatMessage = (content: string) => {
    // Strip whitespace and ensure bullet points have proper line breaks
    let formatted = content.trim();
    
    // Convert bullet point variations to markdown format
    // Handle • bullet points
    formatted = formatted.replace(/^•\s+/gm, '- ');
    formatted = formatted.replace(/\n•\s+/g, '\n- ');
    
    // Ensure bullet points that aren't at line start get a newline
    formatted = formatted.replace(/([^\n])(\s*)(•)/g, '$1\n- ');
    
    // Convert emoji headers (🎯, 🔧, ✅, ⚠️, 🦷, 📋) to proper markdown headers
    formatted = formatted.replace(/^(🎯|🔧|✅|⚠️|🦷|📋)\s+(.+?)(?=:|$)/gm, '\n### $1 $2');
    
    // Ensure headers have proper spacing
    formatted = formatted.replace(/([^\n])\s*(###\s+)/g, '$1\n\n$2');
    
    // Fix bold text that should be headers
    formatted = formatted.replace(/^\*\*(.+?):\*\*$/gm, '### $1');
    formatted = formatted.replace(/\n\*\*(.+?):\*\*/g, '\n\n### $1');
    
    // Special formatting for Description: add line break after the title
    formatted = formatted.replace(/Description:\s*/g, 'Description:\n\n');
    
    // Special formatting for "Selected Treatment:" - add line break after
    formatted = formatted.replace(/(✅\s*Selected Treatment:[^\n]*)(\n)?([^\n])/g, '$1\n\n$3');
    
    // Remove bold formatting from description content (text between Description: and next section)
    formatted = formatted.replace(/Description:\n\n\*\*(.+?)\*\*/g, 'Description:\n\n$1');
    
    // Ensure spacing between sections
    formatted = formatted.replace(/(\n-\s+.+)(\n(?!-\s))/g, '$1\n$2');
    
    return formatted;
  };

  return (
    <div className="h-full flex flex-col bg-medical-50">
      
      {/* Header with Reset Button */}
      {messages.length > 0 && (
        <div className="flex justify-end px-4 py-2 border-b border-medical-200 bg-white">
          <button
            onClick={() => {
              if (window.confirm('Are you sure you want to clear the chat? This will start a new conversation.')) {
                // Call the reset function from parent or fallback to reload
                if (onResetChat) {
                  onResetChat();
                } else {
                  window.location.reload();
                }
              }
            }}
            className="flex items-center gap-2 px-3 py-1.5 text-sm text-medical-600 hover:text-medical-800 hover:bg-medical-50 rounded-lg transition-colors"
            title="Clear chat and start over"
          >
            <RefreshCw className="w-4 h-4" />
            <span>Clear Chat</span>
          </button>
        </div>
      )}

      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto px-2 sm:px-4 md:px-6 lg:px-8 py-4 sm:py-6 md:py-8">
        {messages.length > 0 ? (
          <div className="max-w-4xl mx-auto space-y-4 sm:space-y-6">
            {messages.map((message, index) => (
              <div key={message.id}>
                {/* Add separator ONLY for Ask Questions mode */}
                {isAskQuestionsMode && index > 0 && (messages[index - 1]?.role === 'user' || messages[index - 1]?.sender === 'user') && (message.role === 'assistant' || message.sender === 'bot') && (
                  <div className="flex items-center justify-center my-6">
                    <div className="flex-1 h-px bg-medical-200"></div>
                    <span className="px-4 text-xs text-medical-400 font-medium">Q&A</span>
                    <div className="flex-1 h-px bg-medical-200"></div>
                  </div>
                )}
                
                <div
                  className={`flex w-full ${(message.role === 'user' || message.sender === 'user') ? 'justify-end' : 'justify-start'} mb-4`}
                >
                  {/* Add icon ONLY for Ask Questions mode */}
                  {isAskQuestionsMode && (message.role === 'assistant' || message.sender === 'bot') && (
                    <div className="flex-shrink-0 mr-3 mt-1">
                      <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center">
                        <Bot className="w-5 h-5 text-primary-600" />
                      </div>
                    </div>
                  )}
                  
                  <div
                    className={`
                      max-w-[70%] sm:max-w-[65%] md:max-w-[60%]
                      ${(message.role === 'user' || message.sender === 'user')
                        ? 'chat-message-user'
                        : isAskQuestionsMode
                          ? 'bg-white border border-medical-200 text-medical-800 p-4 rounded-lg shadow-sm'
                          : 'chat-message-assistant'
                      }
                    `}
                  >
                  {/* Add question label ONLY for Ask Questions mode */}
                  {isAskQuestionsMode && (message.role === 'user' || message.sender === 'user') && (
                    <div className="flex items-start space-x-2 mb-2">
                      <MessageCircle className="w-4 h-4 text-primary-500 mt-0.5" />
                      <span className="text-xs font-medium text-primary-500 uppercase">Your Question</span>
                    </div>
                  )}
                  
                  {/* Render images if present */}
                  {message.images && message.images.length > 0 && (
                    <div className="mb-3 space-y-2">
                      {message.images.map((image, index) => (
                        <div key={index} className="relative">
                          <img 
                            src={image.data} 
                            alt={image.name}
                            className="max-w-full rounded-lg shadow-md hover:shadow-lg transition-shadow cursor-pointer"
                            onClick={() => window.open(image.data, '_blank')}
                          />
                          <div className="text-xs mt-1 opacity-75">{image.name}</div>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {(message.role === 'assistant' || message.sender === 'bot') ? (
                    message.isTyping ? (
                      <div className="flex items-center space-x-2">
                        <span className="text-medical-600">{message.content}</span>
                        <div className="typing-indicator inline-flex">
                          <div className="typing-dot"></div>
                          <div className="typing-dot"></div>
                          <div className="typing-dot"></div>
                        </div>
                      </div>
                    ) : (
                      <ReactMarkdown
                        className="prose prose-sm max-w-none"
                        components={{
                          p: ({ children }: any) => <p className="mb-3 last:mb-0 leading-relaxed">{children}</p>,
                          ul: ({ children }: any) => (
                            <ul className="list-none mb-4 mt-2 space-y-2 ml-4">
                              {children}
                            </ul>
                          ),
                          ol: ({ children }: any) => (
                            <ol className="list-decimal list-inside mb-4 mt-2 space-y-2 ml-4">
                              {children}
                            </ol>
                          ),
                          li: ({ children }: any) => (
                            <li className="flex items-start">
                              <span className="text-medical-600 mr-2 mt-0.5">•</span>
                              <span className="flex-1">{children}</span>
                            </li>
                          ),
                          strong: ({ children }: any) => <strong className="font-semibold text-medical-900">{children}</strong>,
                          em: ({ children }: any) => <em className="italic text-medical-700">{children}</em>,
                          h1: ({ children }: any) => (
                            <h1 className="text-xl font-bold mb-4 mt-4 text-medical-900 border-b border-medical-200 pb-2">
                              {children}
                            </h1>
                          ),
                          h2: ({ children }: any) => (
                            <h2 className="text-lg font-semibold mb-3 mt-4 text-medical-800">
                              {children}
                            </h2>
                          ),
                          h3: ({ children }: any) => {
                            // Check if this is a Description or Selected Treatment header
                            const childText = React.Children.toArray(children).join('');
                            const isDescription = childText.includes('Description');
                            const isSelectedTreatment = childText.includes('✅');
                            
                            return (
                              <h3 className={`text-base font-semibold text-medical-800 flex items-center ${
                                isDescription ? 'mb-3 mt-4' : 
                                isSelectedTreatment ? 'mb-3 mt-4' : 
                                'mb-2 mt-3'
                              }`}>
                                {children}
                              </h3>
                            );
                          },
                          blockquote: ({ children }: any) => (
                            <blockquote className="border-l-4 border-primary-300 pl-4 py-2 my-3 bg-primary-50 rounded">
                              {children}
                            </blockquote>
                          ),
                          code: ({ inline, children }: any) => (
                            inline 
                              ? <code className="bg-medical-100 px-1 py-0.5 rounded text-sm font-mono">{children}</code>
                              : <pre className="bg-medical-100 p-3 rounded-lg overflow-x-auto my-3"><code className="text-sm font-mono">{children}</code></pre>
                          ),
                          hr: () => <hr className="my-4 border-medical-200" />,
                        }}
                      >
                        {formatMessage(message.content)}
                      </ReactMarkdown>
                    )
                  ) : (
                    <div className="whitespace-pre-wrap">{message.content}</div>
                  )}

                  <div className={`
                    text-xs mt-2 opacity-75
                    ${(message.role === 'user' || message.sender === 'user') ? 'text-primary-100' : 'text-medical-500'}
                  `}>
                    {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </div>
                  </div>
                </div>
              </div>
            ))}

            {/* Typing Indicator - Now handled by isTyping message */}

            <div ref={messagesEndRef} />
          </div>
        ) : !showDraftPlanModal && !showCustomCaseModal && !autoShowDraftPlan ? (
          /* Empty state with prominent action buttons - only show when not in treatment plan mode and modals are closed */
          <div className="h-full flex items-center justify-center">
            <div className="text-center">
              <h2 className="text-2xl font-bold text-medical-900 mb-3">
                {isAskQuestionsMode 
                  ? 'Ask an Orthodontic Question'
                  : 'Welcome to OrthoMind Treatment Planning'
                }
              </h2>
              <p className="text-medical-600 mb-8">
                {isAskQuestionsMode 
                  ? 'Type your question below or select from common topics'
                  : 'Choose how you\'d like to start your treatment planning'
                }
              </p>
              
              {/* Action buttons for non-ask-questions mode */}
              {!isAskQuestionsMode && (
                <div className="flex justify-center space-x-4">
                  <button
                    onClick={() => setShowDraftPlanModal(true)}
                    className="group relative px-8 py-4 bg-primary-600 text-white rounded-lg shadow-lg hover:bg-primary-700 hover:shadow-xl transition-all duration-200 transform hover:scale-105"
                  >
                    <div className="flex items-center space-x-3">
                      <span className="text-2xl">📋</span>
                      <div className="text-left">
                        <div className="font-semibold text-lg">Draft Treatment Plan</div>
                        <div className="text-sm text-primary-100">Use example case</div>
                      </div>
                    </div>
                  </button>
                  
                  <button
                    onClick={() => setShowCustomCaseModal(true)}
                    className="group relative px-8 py-4 bg-white border-2 border-medical-300 text-medical-700 rounded-lg shadow-lg hover:border-primary-500 hover:shadow-xl transition-all duration-200 transform hover:scale-105"
                  >
                    <div className="flex items-center space-x-3">
                      <span className="text-2xl">📝</span>
                      <div className="text-left">
                        <div className="font-semibold text-lg">Describe My Case</div>
                        <div className="text-sm text-medical-500">Enter custom details</div>
                      </div>
                    </div>
                  </button>
                </div>
              )}
              
              <div className="mt-8 text-sm text-medical-500">
                Or type your message in the input field below
              </div>
            </div>
          </div>
        ) : (
          /* Empty chat area when in treatment plan mode or modal is shown */
          <div className="h-full flex items-center justify-center">
            <div className="text-center">
              {autoShowDraftPlan && !showDraftPlanModal && !showCustomCaseModal ? (
                <div className="text-medical-600">
                  <p className="text-lg">Start typing below to begin your treatment planning session</p>
                  <p className="text-sm mt-2 opacity-75">Or use the example case to get started quickly</p>
                </div>
              ) : null}
            </div>
          </div>
        )}
      </div>

      {/* Input Area */}
      <div className="border-t border-medical-200 bg-white p-3 sm:p-4 md:p-5 lg:p-6">
        <div className="max-w-4xl mx-auto">
          {/* Selected Treatment Display */}
          {selectedTreatment && (
            <div className="mb-4 bg-primary-50 border border-primary-200 rounded-lg p-3 flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <span className="text-primary-600">✓</span>
                <span className="text-sm text-primary-700 font-medium">
                  Selected Treatment: {selectedTreatment.label}
                </span>
              </div>
              <span className="text-xs text-primary-600 bg-primary-100 px-2 py-1 rounded">
                Active
              </span>
            </div>
          )}
          
          {/* Generate Treatment Plan Button */}
          {showGeneratePlanButton && !isLoading && (
            <div className="mb-4 flex justify-center">
              <button
                onClick={() => onSendMessage('Generate a detailed treatment plan')}
                className="medical-button px-6 py-3 flex items-center space-x-2 shadow-md hover:shadow-lg"
              >
                <span>🎯</span>
                <span>Generate Treatment Plan</span>
              </button>
            </div>
          )}
          
          {/* New Question Button for Ask Questions mode */}
          {isAskQuestionsMode && messages.length > 0 && !isLoading && (
            <div className="mb-4 flex justify-center">
              <button
                onClick={() => {
                  if (window.confirm('Start a new question? This will clear the current Q&A history.')) {
                    window.location.reload();
                  }
                }}
                className="flex items-center space-x-2 px-4 py-2 text-sm text-medical-600 hover:text-medical-700 hover:bg-medical-50 rounded-lg transition-colors"
              >
                <RefreshCw className="w-4 h-4" />
                <span>New Question</span>
              </button>
            </div>
          )}
          
          <form onSubmit={handleSubmit} className="flex items-end space-x-2 sm:space-x-3 md:space-x-4">
            {/* File Upload */}
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={isLoading}
              className="p-1.5 sm:p-2 md:p-2.5 lg:p-3 text-medical-400 hover:text-medical-600 transition-colors disabled:opacity-50"
              title="Upload image"
            >
              <Paperclip className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6" />
            </button>

            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              onChange={handleFileSelect}
              className="hidden"
            />

            {/* Text Input */}
            <div className="flex-1">
              <textarea
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSubmit(e);
                  }
                }}
                placeholder="Type your message here..."
                disabled={isLoading}
                rows={inputValue.split('\n').length}
                className="medical-input resize-none max-h-32 disabled:opacity-50"
              />
            </div>

            {/* Send Button */}
            <button
              type="submit"
              disabled={!inputValue.trim() || isLoading}
              className="medical-button px-3 sm:px-4 md:px-5 lg:px-6 py-2 md:py-2.5 lg:py-3 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {isLoading ? (
                <Loader className="w-3 h-3 sm:w-4 sm:h-4 md:w-5 md:h-5 animate-spin" />
              ) : (
                <Send className="w-3 h-3 sm:w-4 sm:h-4 md:w-5 md:h-5" />
              )}
            </button>
          </form>

          {/* Footer */}
          <div className="flex items-center justify-center mt-4 space-x-2 sm:space-x-4 text-xs text-medical-500 px-2 flex-wrap">
            <span>OrthoMind v1.0</span>
            <span className="hidden sm:inline">•</span>
            <a 
              href="https://www.orthomind.ai/terms" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="hover:text-medical-700"
            >
              Terms & Conditions
            </a>
            <span className="hidden sm:inline">•</span>
            <a 
              href="https://www.orthomind.ai/privacy" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="hover:text-medical-700"
            >
              Privacy Policy
            </a>
          </div>
        </div>
      </div>

      {/* Case Selection Modal */}
      <CaseSelectionModal
        isOpen={showCaseModal}
        onClose={() => setShowCaseModal(false)}
        onSelectCase={handleCaseSelect}
      />

      {/* Question Templates Modal */}
      {showQuestionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[80vh] overflow-hidden m-4">
            <div className="p-6 border-b border-medical-200">
              <h2 className="text-2xl font-bold text-medical-900">Common Orthodontic Questions</h2>
              <p className="text-medical-600 mt-1">Select a question template or type your own</p>
            </div>
            
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              <div className="grid gap-3">
                {[
                  "What are the best treatment options for Class II malocclusion in adult patients?",
                  "How do I determine if a patient needs extractions for crowding?",
                  "What's the difference between ceramic braces and metal braces in terms of treatment time?",
                  "When should I consider early interceptive treatment for a child?",
                  "What are the indications for using temporary anchorage devices (TADs)?",
                  "How do I manage severe deep bite cases?",
                  "What's the best approach for treating anterior open bite?",
                  "How long should I retain after orthodontic treatment?",
                  "What are the risks and benefits of accelerated orthodontic treatment?",
                  "How do I handle relapse cases after previous orthodontic treatment?"
                ].map((question, index) => (
                  <button
                    key={index}
                    onClick={() => {
                      onSendMessage(question);
                      setShowQuestionModal(false);
                      onCloseQuestionModal?.();
                    }}
                    className="text-left p-4 border border-medical-200 rounded-lg hover:bg-medical-50 hover:border-primary-300 transition-colors"
                  >
                    <p className="text-medical-800">{question}</p>
                  </button>
                ))}
              </div>
            </div>
            
            <div className="p-6 border-t border-medical-200 flex justify-end">
              <button
                onClick={() => {
                  setShowQuestionModal(false);
                  onCloseQuestionModal?.();
                }}
                className="px-4 py-2 text-medical-600 hover:text-medical-800"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Draft Treatment Plan Modal */}
      {showDraftPlanModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden m-4">
            <div className="p-6 border-b border-medical-200">
              <h2 className="text-2xl font-bold text-medical-900">Draft Treatment Plan</h2>
              <p className="text-medical-600 mt-1">Review the case description before sending</p>
            </div>
            
            <div className="p-6">
              <div 
                className="bg-medical-50 border border-medical-200 rounded-lg p-4 mb-4 cursor-pointer hover:bg-medical-100 transition-colors"
                onClick={() => {
                  const exampleCase = "The patient presents with a Class II malocclusion and has all permanent teeth present. There is crowding observed in both the upper and lower anterior regions. Additionally, the patient exhibits maxillary transverse constriction in the premolar region and a pronounced Spee curve on the lower arch. The upper midline is aligned with the facial midline. Based on the patient profile, maxillary distalization is indicated, with a requirement to distalize more than 1mm.";
                  onSendMessage(exampleCase);
                  setShowDraftPlanModal(false);
                }}
              >
                <h3 className="font-semibold text-medical-900 mb-3">Case Description:</h3>
                <p className="text-medical-700 leading-relaxed">
                  The patient presents with a Class II malocclusion and has all permanent teeth present. 
                  There is crowding observed in both the upper and lower anterior regions. 
                  Additionally, the patient exhibits maxillary transverse constriction in the premolar region 
                  and a pronounced Spee curve on the lower arch. The upper midline is aligned with the facial midline. 
                  Based on the patient profile, maxillary distalization is indicated, with a requirement to distalize more than 1mm.
                </p>
                <p className="text-sm text-primary-600 mt-3 font-medium">Click to use this example case</p>
              </div>
              <div className="text-sm text-medical-600 mb-4">
                <p>This example case will be sent to generate a comprehensive treatment plan.</p>
              </div>
            </div>
            
            <div className="p-6 border-t border-medical-200 flex justify-end space-x-3">
              <button
                onClick={() => setShowDraftPlanModal(false)}
                className="px-4 py-2 text-medical-600 hover:text-medical-800"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setShowDraftPlanModal(false);
                  // Just close the modal to show the chat interface
                }}
                className="medical-button px-6 py-2 bg-primary-600 text-white hover:bg-primary-700"
              >
                Describe My Case
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Custom Case Description Modal */}
      {showCustomCaseModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden m-4">
            <div className="p-6 border-b border-medical-200">
              <h2 className="text-2xl font-bold text-medical-900">Describe Your Case</h2>
              <p className="text-medical-600 mt-1">Enter your custom case description</p>
            </div>
            
            <div className="p-6">
              <textarea
                value={customCaseText}
                onChange={(e) => setCustomCaseText(e.target.value)}
                placeholder="Enter your case description here... Include details about malocclusion type, crowding, dental relationships, and any specific treatment considerations."
                className="w-full h-48 p-4 border border-medical-300 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                autoFocus
              />
              <div className="mt-3 text-sm text-medical-600">
                <p>Tip: Include details about:</p>
                <ul className="list-disc list-inside mt-1 text-medical-500">
                  <li>Malocclusion classification</li>
                  <li>Crowding or spacing issues</li>
                  <li>Overjet and overbite measurements</li>
                  <li>Any skeletal discrepancies</li>
                  <li>Patient age and compliance factors</li>
                </ul>
              </div>
            </div>
            
            <div className="p-6 border-t border-medical-200 flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowCustomCaseModal(false);
                  setCustomCaseText('');
                }}
                className="px-4 py-2 text-medical-600 hover:text-medical-800"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (customCaseText.trim()) {
                    onSendMessage(customCaseText);
                    setShowCustomCaseModal(false);
                    setCustomCaseText('');
                  }
                }}
                disabled={!customCaseText.trim()}
                className="medical-button px-6 py-2 bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Send Case
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatInterface;
